import { IconClipboardCheck, IconClipboardList, IconMessageCircle, IconReceipt } from "@tabler/icons-react";
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui";
import { ChatCard } from "@/features/autorizacion/components/ChatCard";
import { PolizasCard } from "@/features/contabilizacion/components/PolizasCard";
import type { SolicitudDetalle } from "@/lib/types";
import { useChatMensajes } from "@/features/autorizacion/hooks/use-chat-mensajes";
import { usePolizas } from "@/features/contabilizacion/hooks/use-polizas";
import { calcularTabInicial, estaEnFlujoAutorizacion } from "@/features/solicitud-detalle/utils/estado-flags";
import { ResumenTab } from "./ResumenTab";
import { AutorizacionTab } from "./AutorizacionTab";

export function DetalleTabs({ sol }: { sol: SolicitudDetalle }) {
  const enFlujo = estaEnFlujoAutorizacion(sol.estado);
  const mostrarContabilizacion = sol.enviadaAContabilizacion;
  const polizasQuery = usePolizas(sol.id, mostrarContabilizacion);
  const mensajesQuery = useChatMensajes(sol.id, enFlujo);

  if (!enFlujo && !mostrarContabilizacion) {
    return <ResumenTab sol={sol} />;
  }
  const defaultTab = calcularTabInicial(sol);
  return (
    <Tabs defaultValue={defaultTab}>
      <TabsList className="mb-4">
        <TabsTrigger value="resumen" className="flex items-center gap-1.5">
          <IconClipboardList size={14} stroke={1.7} /> Resumen
        </TabsTrigger>
        {enFlujo && (
          <TabsTrigger value="autorizacion" className="flex items-center gap-1.5">
            <IconClipboardCheck size={14} stroke={1.7} /> Autorización
            {sol.estado === "RechazadaParaCorreccion" && <DotRojo />}
          </TabsTrigger>
        )}
        {enFlujo && (
          <TabsTrigger value="chat" className="flex items-center gap-1.5">
            <IconMessageCircle size={14} stroke={1.7} /> Chat
            <CountBadge n={mensajesQuery.data?.length ?? 0} />
          </TabsTrigger>
        )}
        {mostrarContabilizacion && (
          <TabsTrigger value="contabilizacion" className="flex items-center gap-1.5">
            <IconReceipt size={14} stroke={1.7} /> Contabilización
            <CountBadge n={polizasQuery.data?.length ?? 0} />
          </TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="resumen"><ResumenTab sol={sol} /></TabsContent>
      {enFlujo && <TabsContent value="autorizacion"><AutorizacionTab sol={sol} /></TabsContent>}
      {enFlujo && <TabsContent value="chat"><ChatCard solicitudId={sol.id} /></TabsContent>}
      {mostrarContabilizacion && (
        <TabsContent value="contabilizacion"><PolizasCard solicitudId={sol.id} /></TabsContent>
      )}
    </Tabs>
  );
}

function CountBadge({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <Badge size="xs" color="multiva" className="ml-0.5 min-w-5 rounded-full">
      {n}
    </Badge>
  );
}

function DotRojo() {
  return <span className="size-2 rounded-full bg-advertencia-600" />;
}
